import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AlertTriangle, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  LoginCard,
  LoginFooter,
  LoginLeftPanel,
  LoginLogo,
  MobileFloatingElements,
  pageVariants,
  CredentialsResult,
  SendOtpResult,
  OtpVerifyResult,
  OtpMethod,
} from '../components/login';

/**
 * Login Page
 * 
 * Main authentication entry point for HAAT Hub.
 * Supports:
 * - HAAT Employees: Google OAuth 2.0 Sign-In (SSO)
 * - Couriers: Three-step authentication (credentials + method selection + OTP)
 * 
 * Security features:
 * - HttpOnly cookie authentication
 * - Role-based login methods
 * - Automatic redirect if already authenticated
 * - Login attempt tracking with account lockout
 * - Two-factor authentication via email or SMS OTP
 * 
 * @example
 * <Route path="/login" element={<Login />} />
 */
const Login: React.FC = () => {
  const {
    isAuthenticated,
    loginWithGoogle,
    loginWithCredentials,
    sendCourierOtp,
    verifyCourierOtp,
    resendCourierOtp,
  } = useAuth();
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showDeactivatedMessage, setShowDeactivatedMessage] = useState(false);
  const [showIpBlockedMessage, setShowIpBlockedMessage] = useState(false);
  const [showActivatedMessage, setShowActivatedMessage] = useState(false);
  const [activationError, setActivationError] = useState<string | null>(null);

  // Check if user was redirected due to account deactivation, IP block, or activation
  useEffect(() => {
    const reason = searchParams.get('reason');
    const activated = searchParams.get('activated');
    const activationErrorParam = searchParams.get('activation_error');

    if (reason === 'deactivated') {
      setShowDeactivatedMessage(true);
    }
    if (reason === 'ip_blocked') {
      setShowIpBlockedMessage(true);
    }

    if (activated === 'true') {
      setShowActivatedMessage(true);
    }

    if (activationErrorParam) {
      if (activationErrorParam === 'already_activated') {
        setShowActivatedMessage(true);
      } else if (activationErrorParam === 'invalid_token') {
        setActivationError('Invalid or expired activation link. Please contact your administrator.');
      } else {
        setActivationError('Failed to activate account. Please try again.');
      }
    }

    // Clear the URL parameters after showing the message
    if (reason || activated || activationErrorParam) {
      window.history.replaceState({}, document.title, '/login');
    }
  }, [searchParams]);

  // Redirect to dashboard if already authenticated (unless we were sent here due to IP block)
  useEffect(() => {
    const reason = searchParams.get('reason');
    if (isAuthenticated && reason !== 'ip_blocked') {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate, searchParams]);

  /**
   * Handle courier credentials validation (Step 1)
   */
  const handleCourierCredentials = async (username: string, idNumber: string): Promise<CredentialsResult> => {
    return loginWithCredentials(username, idNumber);
  };

  /**
   * Handle sending OTP via selected method (Step 2)
   */
  const handleSendOtp = async (username: string, idNumber: string, method: OtpMethod): Promise<SendOtpResult> => {
    return sendCourierOtp(username, idNumber, method);
  };

  /**
   * Handle OTP verification (Step 3)
   */
  const handleVerifyOtp = async (username: string, idNumber: string, code: string): Promise<OtpVerifyResult> => {
    return verifyCourierOtp(username, idNumber, code);
  };

  /**
   * Handle OTP resend
   */
  const handleResendOtp = async (username: string, idNumber: string, method: OtpMethod): Promise<SendOtpResult> => {
    return resendCourierOtp(username, idNumber, method);
  };

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      className="min-h-screen flex"
    >
      {/* Left Side - Image Panel (Desktop only) */}
      <LoginLeftPanel />

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          {/* Account Activated Success */}
          {showActivatedMessage && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-start gap-3"
            >
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-green-800">Account Activated!</h3>
                <p className="text-sm text-green-600 mt-1">
                  Your account has been activated successfully. You can now login.
                </p>
              </div>
              <button
                onClick={() => setShowActivatedMessage(false)}
                className="text-green-400 hover:text-green-600 ml-auto"
                aria-label="Dismiss"
              >
                ×
              </button>
            </motion.div>
          )}

          {/* Activation Error */}
          {activationError && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3"
            >
              <AlertTriangle className="w-5 h-5 text-[#c00035] flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-red-800">Activation Failed</h3>
                <p className="text-sm text-[#c00035] mt-1">
                  {activationError}
                </p>
              </div>
              <button
                onClick={() => setActivationError(null)}
                className="text-red-400 hover:text-[#c00035] ml-auto"
                aria-label="Dismiss"
              >
                ×
              </button>
            </motion.div>
          )}

          {/* Account Deactivated Warning */}
          {showDeactivatedMessage && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3"
            >
              <AlertTriangle className="w-5 h-5 text-[#c00035] flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-red-800">{t('login.deactivated.title')}</h3>
                <p className="text-sm text-[#c00035] mt-1">
                  {t('login.deactivated.body')}
                </p>
              </div>
              <button
                onClick={() => setShowDeactivatedMessage(false)}
                className="text-red-400 hover:text-[#c00035] ml-auto"
                aria-label={t('login.deactivated.dismiss')}
              >
                ×
              </button>
            </motion.div>
          )}

          {/* IP Blocked Warning */}
          {showIpBlockedMessage && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3"
            >
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-amber-800">{t('login.ip_blocked.title')}</h3>
                <p className="text-sm text-amber-700 mt-1">
                  {t('login.ip_blocked.body')}
                </p>
              </div>
              <button
                onClick={() => setShowIpBlockedMessage(false)}
                className="text-amber-500 hover:text-amber-700 ml-auto"
                aria-label={t('login.ip_blocked.dismiss')}
              >
                ×
              </button>
            </motion.div>
          )}

          {/* Logo & Welcome Text */}
          <LoginLogo />

          {/* <p>Test docker</p> */}

          {/* Login Card with Role Selection */}
          <LoginCard
            onGoogleLogin={loginWithGoogle}
            onCourierCredentials={handleCourierCredentials}
            onCourierSendOtp={handleSendOtp}
            onCourierVerifyOtp={handleVerifyOtp}
            onCourierResendOtp={handleResendOtp}
          />

          {/* Copyright Footer */}
          <LoginFooter />
        </div>
      </div>

      {/* Mobile Background Elements */}
      <MobileFloatingElements />
    </motion.div>
  );
};

export default Login;
